// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { init, setUser, captureException as sentryCaptureException } from "@sentry/browser";

function getMetaInfo(name: string): unknown {
  const content = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;
  try {
    return content && JSON.parse(content);
  } catch {
    // Ignore json parse errors
  }
}

export const ENV_INFO = getMetaInfo("env-info") as
  | Readonly<{ name: string; release: string | null }>
  | undefined;

// a secure referral letter link carries its token in the path; never ship it with an error report
const redactPath = (value: unknown) =>
  typeof value === "string"
    ? value.replace(/\/referral-letter\/[^/?#]+/g, "/referral-letter/[FILTERED]")
    : value;

let captureFn: (...params: Parameters<typeof sentryCaptureException>) => void = (error, hint) => {
  console.error(error, hint);
};

if (import.meta.env.PUBLIC_SENTRY_DSN) {
  const environment = ENV_INFO?.name;
  init({
    dsn: import.meta.env.PUBLIC_SENTRY_DSN,
    allowUrls:
      environment === "production"
        ? ["https://app.twelve.ai"]
        : environment === "staging"
          ? ["https://staging.twelve.ai"]
          : undefined,
    environment,
    release: ENV_INFO?.release || undefined,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        for (const key of ["url", "from", "to"])
          breadcrumb.data[key] = redactPath(breadcrumb.data[key]);
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      if (event.request) {
        event.request.url = redactPath(event.request.url) as string | undefined;
        if (event.request.headers?.["Referer"])
          event.request.headers["Referer"] = redactPath(event.request.headers["Referer"]) as string;
      }
      event.transaction = redactPath(event.transaction) as string | undefined;
      const message = (hint.originalException as null | { message?: string })?.message;
      switch (message) {
        case "The user aborted a request.": {
          console.warn(message);
          return null;
        }
      }
      return event;
    },
  });
  captureFn = sentryCaptureException;

  const userInfo = getMetaInfo("user-info") as { id: string; email: string } | undefined;
  if (userInfo) {
    setUser(userInfo);
  }
}

export const captureException = captureFn;
